import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Switch, FormControlLabel,
  Snackbar, Alert, Select, MenuItem, FormControl, InputLabel, Tabs, Tab,
  Card, CardContent, CardActions, Divider, Tooltip, List, ListItem,
  ListItemText, ListItemSecondaryAction,
} from '@mui/material';
import {
  Add, Delete, Edit, Download, Upload, PlayArrow, ArrowForward,
  QuestionAnswer, DragIndicator, ContentCopy,
} from '@mui/icons-material';
import api from '../../api/client';

interface Script {
  id: number;
  name: string;
  description: string;
  call_type_code: string;
  is_default: boolean;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

interface ScriptNode {
  id: number;
  script_id: number;
  node_type: string;
  sort_order: number;
  question_text: string;
  input_type: string;
  options: { choices?: string[] } | null;
  narrative_template: string;
  is_required: boolean;
  help_text: string;
}

interface ScriptEdge {
  id: number;
  script_id: number;
  from_node_id: number;
  to_node_id: number;
  condition: Record<string, any> | null;
  priority: number;
}

const NODE_TYPE_COLORS: Record<string, string> = {
  question: '#2196f3',
  action: '#ff9800',
  info: '#4caf50',
  end: '#9e9e9e',
};

const INPUT_TYPES = ['yes_no', 'multiple_choice', 'text', 'numeric', 'select'];

export default function CaseEntryPage() {
  const [scripts, setScripts] = useState<Script[]>([]);
  const [selectedScript, setSelectedScript] = useState<Script | null>(null);
  const [nodes, setNodes] = useState<ScriptNode[]>([]);
  const [edges, setEdges] = useState<ScriptEdge[]>([]);
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({ open: false, msg: '', severity: 'success' });

  // Script dialog
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [editingScript, setEditingScript] = useState<Script | null>(null);
  const [scriptForm, setScriptForm] = useState({ name: '', description: '', call_type_code: '', is_default: false, is_active: true });

  // Node dialog
  const [nodeDialogOpen, setNodeDialogOpen] = useState(false);
  const [editingNode, setEditingNode] = useState<ScriptNode | null>(null);
  const [nodeForm, setNodeForm] = useState({
    node_type: 'question', sort_order: 0, question_text: '', input_type: 'text',
    options_text: '', narrative_template: '', is_required: true, help_text: '',
  });

  // Edge dialog
  const [edgeDialogOpen, setEdgeDialogOpen] = useState(false);
  const [editingEdge, setEditingEdge] = useState<ScriptEdge | null>(null);
  const [edgeForm, setEdgeForm] = useState({
    from_node_id: 0, to_node_id: 0, condition_type: 'none', condition_value: '', priority: 0,
  });

  // Import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const loadScripts = useCallback(() => {
    api.get('/case-entry/scripts').then((r) => setScripts(r.data)).catch(() => {});
  }, []);

  const loadScriptDetails = useCallback((script: Script) => {
    api.get(`/case-entry/scripts/${script.id}/nodes`).then((r) => setNodes(r.data));
    api.get(`/case-entry/scripts/${script.id}/edges`).then((r) => setEdges(r.data));
  }, []);

  useEffect(() => { loadScripts(); }, [loadScripts]);

  useEffect(() => {
    if (selectedScript) loadScriptDetails(selectedScript);
  }, [selectedScript, loadScriptDetails]);

  const showMsg = (msg: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, msg, severity });
  };

  // ─── Script handlers ────────────────────────────────────────────────────────
  const openScriptDialog = (script?: Script) => {
    if (script) {
      setEditingScript(script);
      setScriptForm({ name: script.name, description: script.description, call_type_code: script.call_type_code, is_default: script.is_default, is_active: script.is_active });
    } else {
      setEditingScript(null);
      setScriptForm({ name: '', description: '', call_type_code: '', is_default: false, is_active: true });
    }
    setScriptDialogOpen(true);
  };

  const saveScript = async () => {
    try {
      if (editingScript) {
        const r = await api.patch(`/case-entry/scripts/${editingScript.id}`, scriptForm);
        setSelectedScript(r.data);
      } else {
        await api.post('/case-entry/scripts', scriptForm);
      }
      setScriptDialogOpen(false);
      loadScripts();
      showMsg('Script saved');
    } catch { showMsg('Error saving script', 'error'); }
  };

  const deleteScript = async (id: number) => {
    if (!window.confirm('Delete this entire script and all its questions? This cannot be undone.')) return;
    try {
      await api.delete(`/case-entry/scripts/${id}`);
      if (selectedScript?.id === id) { setSelectedScript(null); setNodes([]); setEdges([]); }
      loadScripts();
      showMsg('Script deleted');
    } catch { showMsg('Error deleting script', 'error'); }
  };

  const exportScript = async (id: number) => {
    try {
      const r = await api.get(`/case-entry/scripts/${id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${r.data.name.replace(/\s+/g, '_')}_script.json`;
      a.click();
      URL.revokeObjectURL(url);
      showMsg('Script exported');
    } catch { showMsg('Error exporting', 'error'); }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await api.post('/case-entry/scripts/import', data);
      loadScripts();
      setImportDialogOpen(false);
      showMsg('Script imported successfully');
    } catch { showMsg('Error importing script — check file format', 'error'); }
  };

  // ─── Node handlers ──────────────────────────────────────────────────────────
  const openNodeDialog = (node?: ScriptNode) => {
    if (node) {
      setEditingNode(node);
      setNodeForm({
        node_type: node.node_type,
        sort_order: node.sort_order,
        question_text: node.question_text,
        input_type: node.input_type,
        options_text: node.options?.choices?.join(', ') || '',
        narrative_template: node.narrative_template,
        is_required: node.is_required,
        help_text: node.help_text,
      });
    } else {
      setEditingNode(null);
      const maxOrder = nodes.length > 0 ? Math.max(...nodes.filter(n => n.node_type !== 'end').map(n => n.sort_order)) : 0;
      setNodeForm({
        node_type: 'question', sort_order: maxOrder + 1, question_text: '', input_type: 'text',
        options_text: '', narrative_template: '', is_required: true, help_text: '',
      });
    }
    setNodeDialogOpen(true);
  };

  const saveNode = async () => {
    if (!selectedScript) return;
    const payload: any = {
      script_id: selectedScript.id,
      node_type: nodeForm.node_type,
      sort_order: nodeForm.sort_order,
      question_text: nodeForm.question_text,
      input_type: nodeForm.input_type,
      options: nodeForm.options_text ? { choices: nodeForm.options_text.split(',').map(s => s.trim()).filter(Boolean) } : null,
      narrative_template: nodeForm.narrative_template,
      is_required: nodeForm.is_required,
      help_text: nodeForm.help_text,
    };
    try {
      if (editingNode) {
        delete payload.script_id;
        await api.patch(`/case-entry/nodes/${editingNode.id}`, payload);
      } else {
        await api.post('/case-entry/nodes', payload);
      }
      setNodeDialogOpen(false);
      loadScriptDetails(selectedScript);
      showMsg('Question saved');
    } catch { showMsg('Error saving question', 'error'); }
  };

  const deleteNode = async (nodeId: number) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await api.delete(`/case-entry/nodes/${nodeId}`);
      if (selectedScript) loadScriptDetails(selectedScript);
      showMsg('Question deleted');
    } catch { showMsg('Error deleting question', 'error'); }
  };

  // ─── Edge handlers ──────────────────────────────────────────────────────────
  const openEdgeDialog = (edge?: ScriptEdge) => {
    if (edge) {
      setEditingEdge(edge);
      let condType = 'none';
      let condVal = '';
      if (edge.condition) {
        if (edge.condition.answer) { condType = 'equals'; condVal = edge.condition.answer; }
        else if (edge.condition.answer_in) { condType = 'in_list'; condVal = edge.condition.answer_in.join(', '); }
        else if (edge.condition.answer_not) { condType = 'not_equals'; condVal = edge.condition.answer_not; }
        else if (edge.condition.answer_contains) { condType = 'contains'; condVal = edge.condition.answer_contains; }
      }
      setEdgeForm({ from_node_id: edge.from_node_id, to_node_id: edge.to_node_id, condition_type: condType, condition_value: condVal, priority: edge.priority });
    } else {
      setEditingEdge(null);
      setEdgeForm({ from_node_id: 0, to_node_id: 0, condition_type: 'none', condition_value: '', priority: 0 });
    }
    setEdgeDialogOpen(true);
  };

  const saveEdge = async () => {
    if (!selectedScript) return;
    let condition: Record<string, any> | null = null;
    if (edgeForm.condition_type === 'equals') condition = { answer: edgeForm.condition_value };
    else if (edgeForm.condition_type === 'in_list') condition = { answer_in: edgeForm.condition_value.split(',').map(s => s.trim()) };
    else if (edgeForm.condition_type === 'not_equals') condition = { answer_not: edgeForm.condition_value };
    else if (edgeForm.condition_type === 'contains') condition = { answer_contains: edgeForm.condition_value };

    const payload = {
      script_id: selectedScript.id,
      from_node_id: edgeForm.from_node_id,
      to_node_id: edgeForm.to_node_id,
      condition,
      priority: edgeForm.priority,
    };
    try {
      if (editingEdge) {
        await api.patch(`/case-entry/edges/${editingEdge.id}`, payload);
      } else {
        await api.post('/case-entry/edges', payload);
      }
      setEdgeDialogOpen(false);
      loadScriptDetails(selectedScript);
      showMsg('Branch rule saved');
    } catch { showMsg('Error saving branch rule', 'error'); }
  };

  const deleteEdge = async (edgeId: number) => {
    if (!window.confirm('Delete this branch rule?')) return;
    try {
      await api.delete(`/case-entry/edges/${edgeId}`);
      if (selectedScript) loadScriptDetails(selectedScript);
      showMsg('Branch rule deleted');
    } catch { showMsg('Error deleting branch rule', 'error'); }
  };

  const getNodeLabel = (nodeId: number) => {
    const n = nodes.find(nd => nd.id === nodeId);
    if (!n) return `#${nodeId}`;
    return `${n.sort_order}. ${n.question_text.slice(0, 40)}${n.question_text.length > 40 ? '...' : ''}`;
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Case Entry Scripts</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<Upload />} variant="outlined" onClick={() => setImportDialogOpen(true)}>Import</Button>
          <Button startIcon={<Add />} variant="contained" onClick={() => openScriptDialog()}>New Script</Button>
        </Box>
      </Box>

      {/* Scripts list */}
      {!selectedScript && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Call Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell width={180} />
              </TableRow>
            </TableHead>
            <TableBody>
              {scripts.map((s) => (
                <TableRow key={s.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedScript(s)}>
                  <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                  <TableCell><Chip size="small" label={s.call_type_code || '—'} /></TableCell>
                  <TableCell><Chip size="small" label={s.is_active ? 'Active' : 'Inactive'} color={s.is_active ? 'success' : 'default'} /></TableCell>
                  <TableCell sx={{ color: 'text.secondary', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.description}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Edit Script"><IconButton size="small" onClick={() => setSelectedScript(s)}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Export"><IconButton size="small" onClick={() => exportScript(s.id)}><Download fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Duplicate"><IconButton size="small" onClick={async () => { await exportScript(s.id); }}><ContentCopy fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteScript(s.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {scripts.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No scripts. Click "New Script" to create one.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Script detail editor */}
      {selectedScript && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button variant="text" onClick={() => setSelectedScript(null)}>← Back to Scripts</Button>
            <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>{selectedScript.name}</Typography>
            <Chip label={selectedScript.is_active ? 'Active' : 'Inactive'} color={selectedScript.is_active ? 'success' : 'default'} size="small" />
            <Button size="small" startIcon={<Edit />} onClick={() => openScriptDialog(selectedScript)}>Edit Info</Button>
            <Button size="small" startIcon={<Download />} onClick={() => exportScript(selectedScript.id)}>Export</Button>
          </Box>

          {selectedScript.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{selectedScript.description}</Typography>
          )}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label={`Questions (${nodes.filter(n => n.node_type !== 'end').length})`} />
            <Tab label={`Branch Rules (${edges.length})`} />
          </Tabs>

          {/* Questions tab */}
          {tab === 0 && (
            <Box>
              <Box sx={{ mb: 2, textAlign: 'right' }}>
                <Button startIcon={<Add />} variant="contained" size="small" onClick={() => openNodeDialog()}>Add Question</Button>
              </Box>
              {nodes.filter(n => n.node_type !== 'end').length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                  No questions yet. Add your first question to start building the interview flow.
                </Paper>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {nodes.filter(n => n.node_type !== 'end').map((node, idx) => (
                    <Card key={node.id} variant="outlined" sx={{ borderLeft: `4px solid ${NODE_TYPE_COLORS[node.node_type] || '#ccc'}` }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <DragIndicator sx={{ color: 'text.disabled', mt: 0.5 }} />
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip size="small" label={`#${node.sort_order}`} sx={{ fontWeight: 700, minWidth: 36 }} />
                              <Chip size="small" label={node.input_type} variant="outlined" />
                              {node.is_required && <Chip size="small" label="Required" color="error" variant="outlined" sx={{ fontSize: 10 }} />}
                            </Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>{node.question_text}</Typography>
                            {node.options?.choices && (
                              <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {node.options.choices.map((c, i) => (
                                  <Chip key={i} size="small" label={c} variant="outlined" sx={{ fontSize: 11 }} />
                                ))}
                              </Box>
                            )}
                            {node.narrative_template && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Narrative: "{node.narrative_template}"
                              </Typography>
                            )}
                            {node.help_text && (
                              <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.25 }}>
                                Help: {node.help_text}
                              </Typography>
                            )}
                          </Box>
                          <Box>
                            <IconButton size="small" onClick={() => openNodeDialog(node)}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => deleteNode(node.id)}><Delete fontSize="small" /></IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Branch Rules tab */}
          {tab === 1 && (
            <Box>
              <Box sx={{ mb: 2, textAlign: 'right' }}>
                <Button startIcon={<Add />} variant="contained" size="small" onClick={() => openEdgeDialog()}>Add Branch Rule</Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Branch rules control the flow between questions. Without rules, questions follow their sort order.
                Add rules to skip questions or branch based on answers.
              </Typography>
              {edges.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                  No branch rules. Questions will follow their sort order (linear flow).
                </Paper>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Condition</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>To</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                        <TableCell width={80} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {edges.map((edge) => (
                        <TableRow key={edge.id}>
                          <TableCell sx={{ fontSize: 12 }}>{getNodeLabel(edge.from_node_id)}</TableCell>
                          <TableCell>
                            {edge.condition ? (
                              <Chip size="small" label={
                                edge.condition.answer ? `= "${edge.condition.answer}"` :
                                edge.condition.answer_in ? `in [${edge.condition.answer_in.join(', ')}]` :
                                edge.condition.answer_not ? `≠ "${edge.condition.answer_not}"` :
                                edge.condition.answer_contains ? `contains "${edge.condition.answer_contains}"` : 'Custom'
                              } color="info" variant="outlined" />
                            ) : (
                              <Chip size="small" label="Default (fallback)" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{getNodeLabel(edge.to_node_id)}</TableCell>
                          <TableCell>{edge.priority}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => openEdgeDialog(edge)}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => deleteEdge(edge.id)}><Delete fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Script Dialog */}
      <Dialog open={scriptDialogOpen} onClose={() => setScriptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingScript ? 'Edit Script' : 'New Script'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" margin="normal" value={scriptForm.name} onChange={(e) => setScriptForm({ ...scriptForm, name: e.target.value })} placeholder="e.g. Structure Fire" />
          <TextField fullWidth label="Description" margin="normal" multiline rows={2} value={scriptForm.description} onChange={(e) => setScriptForm({ ...scriptForm, description: e.target.value })} />
          <TextField fullWidth label="Call Type Code (optional)" margin="normal" value={scriptForm.call_type_code} onChange={(e) => setScriptForm({ ...scriptForm, call_type_code: e.target.value })} placeholder="e.g. FIRE, MED, MVA" helperText="Link to a call type to auto-suggest this script" />
          <FormControlLabel control={<Switch checked={scriptForm.is_active} onChange={(_, c) => setScriptForm({ ...scriptForm, is_active: c })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScriptDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveScript} disabled={!scriptForm.name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Node Dialog */}
      <Dialog open={nodeDialogOpen} onClose={() => setNodeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingNode ? 'Edit Question' : 'Add Question'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Question Text" margin="normal" multiline rows={2} value={nodeForm.question_text} onChange={(e) => setNodeForm({ ...nodeForm, question_text: e.target.value })} placeholder="e.g. Is anyone trapped?" />
          <FormControl fullWidth margin="normal">
            <InputLabel>Input Type</InputLabel>
            <Select value={nodeForm.input_type} label="Input Type" onChange={(e) => setNodeForm({ ...nodeForm, input_type: e.target.value })}>
              <MenuItem value="yes_no">Yes / No</MenuItem>
              <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
              <MenuItem value="select">Dropdown Select</MenuItem>
              <MenuItem value="text">Free Text</MenuItem>
              <MenuItem value="numeric">Numeric</MenuItem>
            </Select>
          </FormControl>
          {(nodeForm.input_type === 'multiple_choice' || nodeForm.input_type === 'select' || nodeForm.input_type === 'yes_no') && (
            <TextField
              fullWidth label="Options (comma-separated)" margin="normal"
              value={nodeForm.options_text}
              onChange={(e) => setNodeForm({ ...nodeForm, options_text: e.target.value })}
              placeholder="Yes, No, Unknown"
              helperText="Enter choices separated by commas"
            />
          )}
          <TextField fullWidth label="Narrative Template" margin="normal" value={nodeForm.narrative_template} onChange={(e) => setNodeForm({ ...nodeForm, narrative_template: e.target.value })} placeholder="Entrapment: {answer}" helperText="Use {answer} as placeholder for the dispatcher's response" />
          <TextField fullWidth label="Sort Order" type="number" margin="normal" value={nodeForm.sort_order} onChange={(e) => setNodeForm({ ...nodeForm, sort_order: Number(e.target.value) })} />
          <TextField fullWidth label="Help Text (optional)" margin="normal" value={nodeForm.help_text} onChange={(e) => setNodeForm({ ...nodeForm, help_text: e.target.value })} placeholder="Guidance for the dispatcher" />
          <FormControlLabel control={<Switch checked={nodeForm.is_required} onChange={(_, c) => setNodeForm({ ...nodeForm, is_required: c })} />} label="Required" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNodeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveNode} disabled={!nodeForm.question_text}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edge Dialog */}
      <Dialog open={edgeDialogOpen} onClose={() => setEdgeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEdge ? 'Edit Branch Rule' : 'Add Branch Rule'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define when to jump to a different question based on the answer.
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>From Question</InputLabel>
            <Select value={edgeForm.from_node_id} label="From Question" onChange={(e) => setEdgeForm({ ...edgeForm, from_node_id: Number(e.target.value) })}>
              {nodes.filter(n => n.node_type !== 'end').map((n) => (
                <MenuItem key={n.id} value={n.id}>{n.sort_order}. {n.question_text.slice(0, 50)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Condition</InputLabel>
            <Select value={edgeForm.condition_type} label="Condition" onChange={(e) => setEdgeForm({ ...edgeForm, condition_type: e.target.value })}>
              <MenuItem value="none">Always (default/fallback)</MenuItem>
              <MenuItem value="equals">Answer equals</MenuItem>
              <MenuItem value="in_list">Answer is one of</MenuItem>
              <MenuItem value="not_equals">Answer is NOT</MenuItem>
              <MenuItem value="contains">Answer contains</MenuItem>
            </Select>
          </FormControl>
          {edgeForm.condition_type !== 'none' && (
            <TextField
              fullWidth label={edgeForm.condition_type === 'in_list' ? 'Values (comma-separated)' : 'Value'}
              margin="normal"
              value={edgeForm.condition_value}
              onChange={(e) => setEdgeForm({ ...edgeForm, condition_value: e.target.value })}
              placeholder={edgeForm.condition_type === 'in_list' ? 'Yes, Unknown' : 'Yes'}
            />
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel>Go To Question</InputLabel>
            <Select value={edgeForm.to_node_id} label="Go To Question" onChange={(e) => setEdgeForm({ ...edgeForm, to_node_id: Number(e.target.value) })}>
              {nodes.map((n) => (
                <MenuItem key={n.id} value={n.id}>
                  {n.node_type === 'end' ? '(End Script)' : `${n.sort_order}. ${n.question_text.slice(0, 50)}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField fullWidth label="Priority" type="number" margin="normal" value={edgeForm.priority} onChange={(e) => setEdgeForm({ ...edgeForm, priority: Number(e.target.value) })} helperText="Higher priority rules are checked first (0 = default)" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdgeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdge} disabled={!edgeForm.from_node_id || !edgeForm.to_node_id}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Script</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a JSON file exported from another WatchCenter CAD tenant.
          </Typography>
          <Button variant="outlined" component="label" startIcon={<Upload />}>
            Select File
            <input type="file" hidden accept=".json" onChange={handleImport} />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
